import { useQuery } from '@tanstack/react-query';
import { fetchUsers, User } from '../../lib/api/users';
import { fetchNativeUsers } from '@/entities/auth/api/users';
import { useAuthStore } from '@/store/auth';
import { useUserUtils } from './useUserActions';

/**
 * Идентификатор пользователя может быть числовым (Bitrix, legacy `/users`)
 * или UUID (нативный справочник `/v2/users`). Обе сессии живут параллельно,
 * поэтому резолв имени принимает оба вида.
 */
type AnyUserId = number | string | null | undefined;

export const useUserLocal = {
  // `enabled: false` нужен экранам нового бэка: там legacy-группы `/users`
  // нет вовсе, и запрос отвечал бы 404 на каждой карточке.
  useAllUsers: (options?: { enabled?: boolean }) => {
    return useQuery({
      queryKey: ['all-users'],
      queryFn: () => fetchUsers(''), // Пустая строка = все пользователи
      staleTime: 5 * 60 * 1000, // 5 минут
      enabled: options?.enabled ?? true,
    });
  },

  // Хук для поиска пользователя по ID в локальном списке
  useUserById: (id: number | null): User | null => {
    const { data: allUsersData } = useUserLocal.useAllUsers();
    const allUsers = allUsersData?.result || [];

    if (!id) {
      return null;
    }
    return allUsers.find((user) => user.ID === id) || null;
  },

  // Хук для получения отображаемого имени по ID
  useDisplayNameById: (id: number | null): string => {
    const user = useUserLocal.useUserById(id);
    return useUserUtils.getDisplayName(user);
  },

  // Хук для получения Map всех пользователей (для оптимизации)
  useUsersMap: () => {
    // Под нативной сессией legacy-группы /users на бэке нет (удалена вместе с
    // Bitrix-слоем) — запрос дал бы 404 на каждой странице, где есть шапка.
    const isNativeSession = useAuthStore((state) => Boolean(state.nativeUser));
    const { data: allUsersData, isLoading } = useUserLocal.useAllUsers({
      enabled: !isNativeSession,
    });
    const allUsers = allUsersData?.result || [];

    // Нативный справочник подтягиваем только под нативной сессией: под
    // Bitrix-сессией он не нужен, а лишний запрос на core-бинарь — нет.
    const { data: nativeUsersData, isLoading: nativeLoading } = useQuery({
      queryKey: ['native-users', 'active'],
      queryFn: () => fetchNativeUsers(),
      enabled: isNativeSession,
      staleTime: 5 * 60 * 1000,
    });

    const usersMap = new Map(allUsers.map((user) => [user.ID, user]));
    const nativeNames = new Map(
      (nativeUsersData?.result ?? []).map((user) => [
        user.id,
        user.displayName || user.email,
      ])
    );

    /**
     * Возвращает legacy-пользователя. Для UUID его нет и быть не может —
     * у нативного справочника другая модель, отсюда `null` (аватарка при
     * этом всё равно пустая: `/v2/users` отдаёт `avatarUrl: null`).
     */
    const getUserById = (id: AnyUserId): User | null => {
      return typeof id === 'number' ? usersMap.get(id) || null : null;
    };

    const getDisplayNameById = (id: AnyUserId): string => {
      if (typeof id === 'string') {
        return nativeNames.get(id) ?? '';
      }

      return useUserUtils.getDisplayName(getUserById(id));
    };

    return {
      usersMap,
      isLoading: isNativeSession ? nativeLoading : isLoading,
      getUserById,
      getDisplayNameById,
    };
  },
};
